import { DateTime } from 'luxon';
import {  ChartConfig, ChartDatasetsObject } from "@/app/model/chart";
import { getTzAbbrForce } from '@/app/common/common';
import { TranslateService } from '@ngx-translate/core';
import { Chart } from 'chart.js';
import {NodePerformance, Product} from "@/app/model";
import {EventAPI} from "@/app/model/interface/event";
import {EventNodeAPI} from "@/app/model/interface/event-node";

export function customTooltip(context: any, translate: TranslateService) {
  const tooltipModel = context.tooltip;

  let tooltipEl = document.getElementById("chartjs-tooltip");

  // Create tooltip element if not exists
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.background = "var(--Background-surface-elevation, #FFFF)";
    tooltipEl.style.borderRadius = "16px";
    tooltipEl.style.color = "#313B49;";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.minWidth = "152px";
    tooltipEl.style.padding = "var(--size-spacing-density-1, 16px)";
    tooltipEl.style.gap= "var(--size-spacing-density-1, 16px)";
    tooltipEl.style.transition = "all 0.1s ease";
    tooltipEl.style.zIndex = "95";
    tooltipEl.style.boxShadow = "0 0 16px 0 rgba(102, 119, 144, 0.40)";
    tooltipEl.after
    document.body.appendChild(tooltipEl);
  }

  const { chart, tooltip } = context;

  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = '0';
    return;
  }

  tooltipEl.style.opacity = '1';
  tooltipEl.style.position = 'absolute';

  // Set text content
  const titleLines = tooltipModel.title || [];
  const bodyLines = tooltipModel.body || [];

  let html = "";
  titleLines.forEach((title: string) => {
    html += `
              <div style="
                color: var(--neutrals-neutral-dark, #313B49);
                font-family: var(--neutral-font-family);
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: 18px;
                letter-spacing: 0px;
                display: flex;
                align-items: flex-start;
                gap: 10px;
                flex: 1 0 0;"> ${title}
              </div>
              <dsy-divider divider-orientation="horizontal" divider-color="light-grey"
                    style="width: 120px; height: 1px; background: #C2CDDD;" class="divider"></dsy-divider>`;
  });
  removeRepeated(bodyLines).forEach((body: any) => {
    html += `<div style="align-self: stretch;"><span style="
                            color: var(--neutrals-neutral-dark, #313B49);
                            font-family: var( --neutral-font-family-black);
                            font-size: 12px;
                            font-style: normal;
                            font-weight: 700;
                            line-height: 18px;">${body.lines[0]}</span>
                          <span style="
                            color: var(--neutrals-neutral-dark, #313B49);
                            font-family: var(--neutral-font-family);
                            font-size: 12px;
                            font-style: normal;
                            font-weight: 400;
                            line-height: 18px;">${body.lines[1]}</span>
                          <span style="
                              color: var(--neutrals-neutral-dark, #313B49);
                              font-family: var(--neutral-font-family);
                              font-size: 12px;
                              font-style: normal;
                              font-weight: 400;
                              line-height: 18px;">${body.lines[2]}</span>                          
                  
                  </div>`;
  });
  if(removeRepeated(bodyLines)[0].lines[3]) {
      html += `<div style="align-self: stretch;">
                          <span style="
                              color: var(--neutrals-neutral-dark, #313B49);
                              font-family: var( --neutral-font-family-black);
                              font-size: 12px;
                              font-style: normal;
                              font-weight: 700;
                              line-height: 18px;">${translate.instant("INTERVAL_GRAPH.TOOLTIP_PERF")}</span>
                          <span style="
                              color: var(--neutrals-neutral-dark, #313B49);
                              font-family: var(--neutral-font-family);
                              font-size: 12px;
                              font-style: normal;
                              font-weight: 400;
                              line-height: 18px;">${bodyLines[0].lines[3]}</span>
            </div>`
    }
  tooltipEl.innerHTML = html;

  // Positioning logic
  const canvasRect = chart.canvas.getBoundingClientRect();
  
  const { top: chartTop, bottom: chartBottom } = chart.chartArea;
  
  //Vertical center point
  const midY = (chartTop + chartBottom) / 2;
  
  // Gap between cursor and tooltip
  const offset = 30; 

  let top = 0;

  if (tooltip.caretY < midY) {
    // BELOW the cursor
    top = canvasRect.top + window.scrollY + tooltip.caretY + offset;
  } else {
    //  ABOVE the cursor
    top = canvasRect.top + window.scrollY + tooltip.caretY - tooltipEl.offsetHeight - offset;
  }

  // Horizontal positioning (centered on cursor)
  const left = canvasRect.left + window.scrollX + tooltipModel.caretX - (tooltipEl.offsetWidth / 2);

  tooltipEl.style.top = `${top}px`;
  tooltipEl.style.left = `${left}px`;
}

export const removeRepeated = (bodyLines: any) => {
  const unique = [];
  const seen = new Set();

  for (const item of bodyLines) {
      const key = JSON.stringify(item.lines[0]);
      if (!seen.has(key)) {
          seen.add(key);
          unique.push(item);
      }
  }
  return unique;
}
export const getNumericValue = (value: string | undefined): number =>
  Number(value?.replaceAll(",", "."));

const normalizeDisplayValue = (value: number): number => {
  if (Object.is(value, -0)) {
    return 0;
  }

  return Math.abs(value) < 1e-9 ? 0 : value;
};

export const formatValue = (value: number, fractionDigits: number, locale: string): string => {
  return Intl.NumberFormat(
    locale,
    { 
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits
    }
  ).format(normalizeDisplayValue(value));
};
export const xScalesCallback = (
  value: number,
  values: any,
  locale: string,
  event: EventAPI,
  index: number,
  granularity: number,
  hideTimezone = false
) => {
  const timeLabel = DateTime.fromMillis(Number(value), {
      zone: event.full_time_zone,
      locale: locale
    }).toFormat('HH:mm');
  const mins = timeLabel.split(':')[1];
  if (Number(mins) % granularity === 0) {
    return hideTimezone ? timeLabel : `${timeLabel} ${getTzAbbrForce(event.full_time_zone, new Date(Number(value)))}`;
  }
  return undefined;
};

const getMinTickStep = (ticks: any[] | undefined): number | null => {
  if (!ticks || ticks.length < 2) {
    return null;
  }

  const values = ticks
    .map((tick: any) => Number(typeof tick === "object" ? tick.value : tick))
    .filter((tickValue) => Number.isFinite(tickValue))
    .sort((a, b) => a - b);

  if (values.length < 2) {
    return null;
  }

  let minDiff = Number.POSITIVE_INFINITY;
  for (let i = 1; i < values.length; i += 1) {
    const diff = Math.abs(values[i] - values[i - 1]);
    if (diff > 0 && diff < minDiff) {
      minDiff = diff;
    }
  }

  return Number.isFinite(minDiff) ? minDiff : null;
};

const formatKwTick = (value: number, tickStep: number | null, locale?: string): string => {
  let minimumFractionDigits = 0;
  let maximumFractionDigits = 1;

  if (tickStep !== null) {
    if (tickStep < 0.1) {
      minimumFractionDigits = 2;
      maximumFractionDigits = 2;
    } else if (tickStep < 1) {
      minimumFractionDigits = 1;
      maximumFractionDigits = 1;
    } else {
      minimumFractionDigits = 0;
      maximumFractionDigits = 0;
    }
  }

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits
  }).format(normalizeDisplayValue(value));
};

export const yScalesCallback = (
  value: number,
  product: Product,
  eventNode: EventNodeAPI,
  ticks?: any[],
  locale?: string
) => {
  const isMw =
    (product?.prez_conf?.prefered_prez_demand_uom ?? eventNode?.last_current_performance_uom).toLocaleLowerCase() === "mw";
  const rawValue = isMw ? value / 1000 : value;
  if (isMw) {
    return rawValue.toFixed(3);
  }

  const tickStep = getMinTickStep(ticks);
  return formatKwTick(rawValue, tickStep, locale);
};

export const tooltipCallback = (
  context: any,
  event: EventAPI,
  locale: string
) => {
  const timeLabel = DateTime.fromMillis(Number(context[0].parsed.x), {
    zone: event.full_time_zone,
    locale: locale
  }).toFormat('HH:mm');
  const tzAbbr = getTzAbbrForce(event.full_time_zone, new Date(Number(context[0].parsed.x)));

  return `${timeLabel} ${tzAbbr}`;
};

export const labelCallback = (
  context: any,
  product: Product,
  locale: string,
  eventNode: EventNodeAPI,
  eventPerformance: NodePerformance[]
) => {
  const label = context.dataset.label || '';
  const performanceLabel = context.performanceLabel;
  const isKw = (product?.prez_conf?.prefered_prez_demand_uom ?? eventNode.last_current_metered_uom).toLocaleLowerCase() === "kw";
  const powerMeasure = product?.prez_conf?.prefered_prez_demand_uom ?? eventNode.last_current_metered_uom;
  const yValue = normalizeDisplayValue(Number(context.parsed.y));
  const mwValue = yValue / 1000;
  const performVal = getPerformanceValue(context, eventNode, product, locale, eventPerformance);
  const mwFractionDigits = 3; 
  const tooltipFractionDigits = isKw ? 0 : 2;
  const powerValue =
    isKw
      ? Intl.NumberFormat(locale, { maximumFractionDigits: tooltipFractionDigits }).format(
          yValue
        )
      : Intl.NumberFormat(locale, { minimumFractionDigits: mwFractionDigits, maximumFractionDigits: mwFractionDigits }).format(mwValue);
  return [label, powerValue, powerMeasure, performVal, performanceLabel];
};

const getPerformanceValue = (
  context: any,
  eventNode: EventNodeAPI,
  product: Product,
  locale: string,
  eventPerformance: NodePerformance[]
) => {
  const perfValue = getNumericValue(context?.raw?.performance_value?.toString());
  const normalizedPerfValue = normalizeDisplayValue(perfValue);
  const mwValue = normalizedPerfValue / 1000;
  const isKw = (product?.prez_conf?.prefered_prez_demand_uom ?? eventNode?.last_current_metered_uom)
    ?.toLowerCase() === "kw";
  const mwFractionDigits = 3;

  if (Number.isFinite(perfValue)) {
    const rawValue = isKw ? normalizedPerfValue : mwValue;
    const expectedCapacity = eventNode.expected_capacity_value;

    const performancePerCent =
      expectedCapacity
        ? perfValue / expectedCapacity * 100
        : 0;
    return `${isKw 
      ? Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(rawValue)
      : formatValue(rawValue, mwFractionDigits, locale)} ${
      product?.prez_conf?.prefered_prez_demand_uom ?? eventNode?.last_current_metered_uom
    } (${Number(performancePerCent).toFixed(0)}%)`;
  }
  return ``;
}; 

export const yTitleTopPlugin = {
  id: 'yTitleTop',
beforeLayout: (chart: any, args: any, opts: any) => {
    const {
      display,
      font
    } = opts;
    if (!display) {
      return;
    }

    const {
      ctx
    } = chart;
    ctx.font = font;

    const {
      width
    } = ctx.measureText(opts.text);
    chart.options.layout.padding.left = width * 1.1;
  },
  afterDraw: (chart: any, args: any, opts: any) => {
    const { text } = opts;
    const { ctx } = chart;

    if (opts.display) {
      ctx.font = "800 12px 'Roobert ENEL', sans-serif";
      ctx.fillStyle = '#667790';
      ctx.fillText(text, 0, 14);
    }
     
  }
};

export const createTargetLines = (
  label: string,
  target: ChartDatasetsObject[],
  targetLowerBound: ChartDatasetsObject[],
  targetUpperBound: ChartDatasetsObject[],
  gradient: any,
  eventNode: EventNodeAPI,
  product: Product
) => {
  const fillBelowTargetTypes = new Set([
    'LOAD',
    'LOAD_DROP_TO',
    'LOAD_METERED_GENERATOR',
  ]);
  const fillAboveTargetTypes = new Set([
    'GENERATOR',
    'DEMAND_ON',
    'STORAGE',
  ]);

  // Types whose target applies the expected capacity. A negative capacity reverses the
  // instruction, so the compliant side of the target reverses with it. LOAD_DROP_TO is the one
  // exclusion: its target is the baseline, so the capacity never reaches it and its sign cannot
  // change which side complies. STORAGE does apply the capacity and does reverse.
  const usesCapacityTypes = new Set([
    'LOAD',
    'LOAD_METERED_GENERATOR',
    'GENERATOR',
    'DEMAND_ON',
    'STORAGE',
  ]);
  const capacityValue = eventNode.expected_capacity_value ?? eventNode.registered_capacity_value;
  const reversed = usesCapacityTypes.has(eventNode.registration_type) && capacityValue < 0;

  let fill: boolean | 'start' | 'end' = true;
  if (fillBelowTargetTypes.has(eventNode.registration_type)) {
    fill = reversed ? 'end' : 'start';
  } else if (fillAboveTargetTypes.has(eventNode.registration_type)) {
    fill = reversed ? 'start' : 'end';
  }

  let targetObj: ChartConfig[] = [{
    label: label,
    data: target,
    borderColor: "rgb(53,153,143)",
    stepped: false,
    backgroundColor: gradient,
    pointRadius: 0,
    tension: 0,
    lineGroup: true,
    fill,
    borderWidth: 2
  }];

  if (product.target_type === 'RANGE' || product.target_type === 'RANGE_PLUS_HOUR') {
    const targetLowerObj = {
      label: "Target Lower Bound",
      data: targetLowerBound,
      borderColor: "rgb(53,153,143)",
      stepped: false,
      backgroundColor: gradient,
      pointRadius: 0,
      tension: 0,
      lineGroup: true,
      fill: 2,
      borderWidth: 2
    }
    const targetUpperObj = {
      label: "Target Upper Bound",
      data: targetUpperBound,
      borderColor: "rgb(53,153,143)",
      stepped: false,
      backgroundColor: gradient,
      pointRadius: 0,
      tension: 0,
      lineGroup: true,
      fill: false,
      borderWidth: 2
    }
    targetObj = [targetUpperObj, targetLowerObj];
  }
  return targetObj;
}

export const verticalHoverLine = {
  id: 'verticalHoverLine',
  beforeDatasetsDraw(chart: Chart) {
    const { ctx } = chart;

    const chartArea = chart.chartArea;
    if (!chartArea) {
      return;
    }

    const { top, bottom } = chartArea;

    const activeElements = chart.getActiveElements
      ? chart.getActiveElements()
      : [];

    if (!activeElements || activeElements.length === 0) {
      return;
    }

    const x = activeElements[0].element.x;

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = '#C2CDDD';
    ctx.lineWidth = 2;
    ctx.moveTo(x, top);
    ctx.lineTo(x, bottom);
    ctx.stroke();
    ctx.restore();
  },
};

export const annotationBoxPlugin = {
  id: 'annotationBoxPlugin',
  beforeDraw(chart: Chart) {
    const { ctx, chartArea } = chart as any;
    const xScale = (chart as any).scales?.x;
    if (!xScale || !chartArea) return;

    const annotations = chart.options?.plugins?.annotation?.annotations || {};
    Object.values(annotations).forEach((annotation: any) => {
      if (!annotation || annotation.type !== 'box') return;

      const xMinVal = annotation.xMin ? new Date(annotation.xMin).getTime() : null;
      const xMaxVal = annotation.xMax ? new Date(annotation.xMax).getTime() : null;
      if (xMinVal == null || xMaxVal == null) return;

      let xMin = xScale.getPixelForValue
        ? xScale.getPixelForValue(xMinVal)
        : xScale.getPixelForValue(xMinVal);
      let xMax = xScale.getPixelForValue
        ? xScale.getPixelForValue(xMaxVal)
        : xScale.getPixelForValue(xMaxVal);

      // Handle case when chart has no data and getPixelForValue returns NaN
      // In this case, use the scale's min/max and calculate pixel position proportionally
      if (isNaN(xMin) || isNaN(xMax)) {
        const scaleMin = xScale.min;
        const scaleMax = xScale.max;
        
        if (scaleMin != null && scaleMax != null) {
          const scaleDuration = scaleMax - scaleMin;
          const chartWidth = chartArea.right - chartArea.left;
          
          // Calculate proportional pixel positions based on time values
          xMin = chartArea.left + ((xMinVal - scaleMin) / scaleDuration) * chartWidth;
          xMax = chartArea.left + ((xMaxVal - scaleMin) / scaleDuration) * chartWidth;
        } else {
          return; // Can't render without valid scale bounds
        }
      }

      // Clamp pixel values to chart area bounds to handle annotations that extend beyond chart boundaries
      const clampedXMin = Math.max(xMin, chartArea.left);
      const clampedXMax = Math.min(xMax, chartArea.right);

      if (isNaN(clampedXMin) || isNaN(clampedXMax) || clampedXMin >= clampedXMax) return;

      ctx.save();
      ctx.fillStyle = annotation.backgroundColor || 'rgba(0,0,0,0.05)';
      const left = Math.min(clampedXMin, clampedXMax);
      const width = Math.abs(clampedXMax - clampedXMin);
      ctx.fillRect(left, chartArea.top, width, chartArea.bottom - chartArea.top);
      ctx.restore();
    });
  }
};
